import { useEffect, useRef, useState } from 'react';

// ============================================================
// Photo drop zone for one Homes card. Three ways in, one way out (onFile):
//   paste    ⌘V anywhere on the page while the zone is open (not while typing
//            in a field), first image/png or image/jpeg on the clipboard
//   drop     drag an image file onto the zone
//   choose   the plain file picker
// The caller uploads (Homes_Photos, HM-xxxxx naming) and closes the zone on
// success. Type is checked again from the file's bytes at upload time.
// Only one zone is open at a time (the section owns which), so a paste can
// never land on two houses.
// ============================================================

const IMAGE_TYPES = ['image/png', 'image/jpeg'];

const typing = (el) => {
  const tag = (el?.tagName || '').toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || el?.isContentEditable === true;
};

/** First pasted/dropped PNG or JPEG as a File, or null. */
export function imageFrom(items, files) {
  for (const it of Array.from(items || [])) {
    if (it.kind === 'file' && IMAGE_TYPES.includes(it.type)) {
      const f = it.getAsFile();
      if (f) return f;
    }
  }
  for (const f of Array.from(files || [])) if (IMAGE_TYPES.includes(f.type)) return f;
  return null;
}

export default function PhotoDropZone({ onFile, onClose, busy }) {
  const [over, setOver] = useState(false);
  const [msg, setMsg] = useState(null);
  const zoneRef = useRef(null);
  const fileRef = useRef(null);
  const take = (f) => {
    if (!f) { setMsg('That is not a PNG or JPEG image.'); return; }
    setMsg(null);
    onFile(f);
  };

  useEffect(() => { zoneRef.current?.focus(); }, []);

  useEffect(() => {
    const onPaste = (e) => {
      if (busy || typing(e.target)) return;
      const cd = e.clipboardData;
      if (!cd) return;
      e.preventDefault();
      take(imageFrom(cd.items, cd.files));
    };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('paste', onPaste);
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('paste', onPaste); window.removeEventListener('keydown', onKey); };
  }); // re-bound every render so `busy` and `onFile` are current

  return (
    <div
      ref={zoneRef}
      tabIndex={-1}
      className={`Homes-dropzone${over ? ' Homes-dropzone--over' : ''}`}
      data-testid="photo-dropzone"
      onDragOver={(e) => { e.preventDefault(); if (!over) setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); if (!busy) take(imageFrom(e.dataTransfer?.items, e.dataTransfer?.files)); }}
    >
      {busy ? (
        <span className="Homes-dropzone-text">Uploading…</span>
      ) : (
        <span className="Homes-dropzone-text">
          Paste (⌘V) or drop an image, or{' '}
          <button type="button" className="Homes-link" onClick={() => fileRef.current?.click()}>choose a file</button>
        </span>
      )}
      {msg && <span className="Homes-dropzone-msg">{msg}</span>}
      <button type="button" className="Homes-dropzone-close" onClick={onClose} aria-label="Close photo drop zone">✕</button>
      <input ref={fileRef} type="file" accept="image/jpeg,image/png" hidden data-testid="photo-file"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) take(IMAGE_TYPES.includes(f.type) || !f.type ? f : null); }} />
    </div>
  );
}
